import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useAuthStore } from '@/store/authStore';
import Sidebar from '@/components/layout/Sidebar';
import Login from '@/components/auth/Login';
import Sites from '@/components/Sites';
import Products from '@/components/Products';
import Sync from '@/components/Sync';
import MasterSlaveIntegrations from '@/components/MasterSlaveIntegrations';
import SynchronizationJobs from '@/components/SynchronizationJobs';
import IntegrationStatuses from '@/components/IntegrationStatuses';

// Settings components
import {
  FortnoxAuthentication,
  FortnoxIntegration,
  FortnoxReconciliation,
  FortnoxInvoicePayment,
  FortnoxProductSynchronizationSettings,
  FortnoxOSS,
  FortnoxExportAccount,
  FortnoxGiftcardAccount,
  FortnoxCustomerDebtAccounts,
  FortnoxOrderListener,
  FortnoxInvoiceListener,
  KustomAuthentication,
  KustomIntegration,
  LedyerAuthentication,
  LedyerIntegration,
  RentmanAuthentication,
  RentmanIntegration,
  SitooAuthentication,
  SitooIntegration,
  VismaAuthentication,
  VismaCustomerDebtAccounts,
  VismaIntegration,
  VismaInvoicePayment,
  VismaInvoiceSettings,
  WooCommerceAuthentication,
  WooCommerceIntegrationTimestamps,
  ShopifyAuthentication,
  ShopifyIntegrationApiPollState,
  ShopifyWebhooks,
  CsvSource,
} from '@/components/settings';

// Data components
import {
  FortnoxProcessedTransactions,
  FortnoxProcessedPayouts,
  VismaProcessedPayouts,
} from '@/components/data';

import Register from '@/components/auth/Register';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
        </Router>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Sidebar />
          <main className="ml-64 p-6">
            <Routes>
              <Route path="/" element={<Navigate to="/sites" replace />} />
              <Route path="/sites" element={<Sites />} />
              <Route path="/products" element={<Products />} />
              <Route path="/sync" element={<Sync />} />
              <Route path="/register" element={<Register />} />
              <Route path="/master_slave_integrations" element={<MasterSlaveIntegrations />} />
              <Route path="/synchronization_jobs" element={<SynchronizationJobs />} />
              <Route path="/integration_statuses" element={<IntegrationStatuses />} />

              {/* Fortnox Routes */}
              <Route path="/settings/fortnox/authentication" element={<FortnoxAuthentication />} />
              <Route path="/settings/fortnox/integration" element={<FortnoxIntegration />} />
              <Route path="/settings/fortnox/reconciliation" element={<FortnoxReconciliation />} />
              <Route path="/settings/fortnox/invoice_payment_account" element={<FortnoxInvoicePayment />} />
              <Route path="/settings/fortnox/product_synchronization_settings" element={<FortnoxProductSynchronizationSettings />} />
              <Route path="/settings/fortnox/oss" element={<FortnoxOSS />} />
              <Route path="/settings/fortnox/export_account" element={<FortnoxExportAccount />} />
              <Route path="/settings/fortnox/giftcard_account" element={<FortnoxGiftcardAccount />} />
              <Route path="/settings/fortnox/customer_debt_accounts" element={<FortnoxCustomerDebtAccounts />} />
              <Route path="/settings/fortnox/order_listener" element={<FortnoxOrderListener />} />
              <Route path="/settings/fortnox/invoice_listener" element={<FortnoxInvoiceListener />} />

              {/* Kustom Routes */}
              <Route path="/settings/kustom/authentication" element={<KustomAuthentication />} />
              <Route path="/settings/kustom/integration" element={<KustomIntegration />} />

              {/* Ledyer Routes */}
              <Route path="/settings/ledyer/authentication" element={<LedyerAuthentication />} />
              <Route path="/settings/ledyer/integration" element={<LedyerIntegration />} />

              {/* Rentman Routes */}
              <Route path="/settings/rentman/authentication" element={<RentmanAuthentication />} />
              <Route path="/settings/rentman/integration" element={<RentmanIntegration />} />

              {/* Sitoo Routes */}
              <Route path="/settings/sitoo/authentication" element={<SitooAuthentication />} />
              <Route path="/settings/sitoo/integration" element={<SitooIntegration />} />

              {/* Visma Routes */}
              <Route path="/settings/visma/authentication" element={<VismaAuthentication />} />
              <Route path="/settings/visma/customer_debt_accounts" element={<VismaCustomerDebtAccounts />} />
              <Route path="/settings/visma/integration" element={<VismaIntegration />} />
              <Route path="/settings/visma/invoice_payment" element={<VismaInvoicePayment />} />
              <Route path="/settings/visma/invoice" element={<VismaInvoiceSettings />} />

              {/* WooCommerce Routes */}
              <Route path="/settings/woocommerce/authentication" element={<WooCommerceAuthentication />} />
              <Route path="/settings/woocommerce/integration_timestamps" element={<WooCommerceIntegrationTimestamps />} />

              {/* Shopify Routes */}
              <Route path="/settings/shopify/authentication" element={<ShopifyAuthentication />} />
              <Route path="/settings/shopify/integration_timestamps" element={<ShopifyIntegrationApiPollState />} />
              <Route path="/settings/shopify/webhooks" element={<ShopifyWebhooks />} />

              {/* CSV Source Routes */}
              <Route path="/settings/csv_source" element={<CsvSource />} />

              {/* Data Routes */}
              <Route path="/user_data/fortnox/processed_transactions" element={<FortnoxProcessedTransactions />} />
              <Route path="/user_data/fortnox/processed_payouts" element={<FortnoxProcessedPayouts />} />
              <Route path="/user_data/visma/processed_payouts" element={<VismaProcessedPayouts />} />

              <Route path="*" element={<Navigate to="/sites" replace />} />
            </Routes>
          </main>
        </div>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
